import axios, { type AxiosError, type AxiosResponse } from "axios";
import { getCookie } from "@/utils/cookieUtils";

const plainAxios = axios.create();

plainAxios.defaults.baseURL = import.meta.env.VITE_FLEETER_BACKEND_API_URL;

plainAxios.interceptors.request.use((config) => {
	// Evita rodar no server-side (Next.js SSR)
	if (typeof window !== "undefined") {
		const token = getCookie("token");

		// config.headers já é uma instância de AxiosHeaders (mutável)
		const headers = config.headers;

		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}

		// Se a API key deve ser enviada sempre (recomendado para APIs públicas/protegidas por key)
		const apiKey = import.meta.env.VITE_FLEETER_BACKEND_API_KEY;
		if (apiKey) {
			headers.set("x-api-key", apiKey);
		}
	}

	return config;
});

export type ApiErrorResponse = {
	code: string;
	statusCode: number;
	message: string;
	detail?: string;
	type?: string;
	errors?: unknown[];
	fieldsErrors?: Record<string, unknown>;
};

function handleResponseError(error: AxiosError): Promise<never> {
	// Erro de rede (sem resposta do servidor)
	if (!error.response) {
		if (error.request) {
			return Promise.reject({
				code: "NETWORK_ERROR",
				statusCode: 0,
				message: "Por favor, verifique sua conexão e tente novamente.",
			} as ApiErrorResponse);
		}

		return Promise.reject({
			code: error.code ?? "UNKNOWN_ERROR",
			statusCode: 0,
			message: error.message,
		} as ApiErrorResponse);
	}

	// Erro com resposta do servidor
	const response = error.response;
	const statusCode = response.status;
	const data = response.data as any;

	// Estrutura padrão da sua API: { error: { detail, type, ... } }
	if (data?.error && typeof data.error === "object") {
		const apiError = data.error;

		return Promise.reject({
			code: apiError.type || "API_ERROR",
			statusCode,
			message: apiError.detail || apiError.title || "Erro desconhecido da API",
			detail: apiError.detail,
			type: apiError.type,
			errors: apiError.errors,
			fieldsErrors: apiError.fieldsErrors,
		} as ApiErrorResponse);
	}

	// Fallback genérico caso a estrutura mude inesperadamente
	return Promise.reject({
		code: "UNEXPECTED_RESPONSE",
		statusCode,
		message: "Erro ao processar a solicitação.",
	} as ApiErrorResponse);
}

// Aplicando o interceptor
plainAxios.interceptors.response.use(
	(response: AxiosResponse) => response,
	handleResponseError
);

export const Axios = plainAxios;
